import type { BrainMessage } from '@/types/brain'

interface MessageCardProps {
  message: BrainMessage
  onAck: (id: string) => void
  onClick: (msg: BrainMessage) => void
  isSelected?: boolean
  isSent?: boolean
}

function timeAgo(timestamp: number | string): string {
  const ms = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp
  const seconds = Math.floor((Date.now() - ms) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function priorityDot(type: string): { color: string; label: string } {
  switch (type) {
    case 'bug_report':
      return { color: 'bg-[#E53935]', label: 'critical' }
    case 'work_order_update':
      return { color: 'bg-amber-500', label: 'high' }
    default:
      return { color: 'bg-swiss-black', label: 'medium' }
  }
}

function typeBadge(type: string): string {
  switch (type) {
    case 'notification': return 'NOTIFICATION'
    case 'status_update': return 'STATUS UPDATE'
    case 'work_order_update': return 'WORK ORDER'
    case 'bug_report': return 'BUG REPORT'
    default: return type.toUpperCase().replace(/_/g, ' ')
  }
}

export function MessageCard({ message, onAck, onClick, isSelected, isSent }: MessageCardProps) {
  const dot = priorityDot(message.type)

  return (
    <div
      className={`flex items-center gap-4 px-4 py-3 border-b border-swiss-gray-200 cursor-pointer hover:bg-swiss-off-white transition-colors duration-100 border-l-2 border-l-swiss-black ${isSelected ? 'bg-swiss-off-white' : ''}`}
      onClick={() => onClick(message)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(message) } }}
      aria-label={`Message from ${message.from}: ${message.subject}`}
    >
      {/* Priority dot */}
      <span
        className={`shrink-0 w-2 h-2 ${dot.color}`}
        aria-label={dot.label}
        title={dot.label}
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500 font-swiss-mono shrink-0">
            {isSent ? `TO: ${message.to || 'unknown'}` : `FROM: ${message.from}`}
          </span>
          <span className="text-[10px] font-medium uppercase tracking-widest bg-swiss-black text-swiss-white px-1.5 py-0.5 shrink-0">
            {typeBadge(message.type)}
          </span>
        </div>
        <p className="text-[13px] font-semibold truncate">
          {message.subject}
        </p>
      </div>

      {/* Age */}
      <span className="shrink-0 text-[11px] font-swiss-mono text-swiss-gray-500">
        {timeAgo(message.timestamp)}
      </span>

      {/* ACK button */}
      {!isSent && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onAck(message.id)
          }}
          className="shrink-0 text-[11px] font-medium uppercase tracking-widest text-swiss-gray-500 hover:text-swiss-black transition-colors duration-100 cursor-pointer px-1"
          aria-label={`Acknowledge message: ${message.subject}`}
        >
          ACK
        </button>
      )}
    </div>
  )
}
